// src/services/tokenRefresh.js
//
// Trades the refresh token for a new access token via POST /api/auth/refresh.
//
// Two things matter here:
//  1. Single flight — when ten requests 401 at once they must share ONE refresh
//     call, otherwise every one of them rotates the token and nine of the
//     results are thrown away.
//  2. It must never route through the patched window.fetch in SessionContext,
//     or a failing refresh would try to refresh itself.

import { API_BASE } from "config/api";
import {
  getAccessToken,
  getRefreshToken,
  getRefreshSkewMs,
  isAccessTokenExpiring,
  saveTokens,
} from "services/tokenStorage";

// Renew up to this far ahead of the real expiry, so a request is never sent
// with a token that dies in flight. Upper bound only — the effective skew is
// capped at a quarter of the token's lifetime, so short TTLs behave sanely.
export const MAX_REFRESH_SKEW_MS = 5 * 60 * 1000; // 5 minutes

/** Effective lead time for the token currently in storage. */
export const effectiveRefreshSkewMs = () => getRefreshSkewMs(MAX_REFRESH_SKEW_MS);

// Captured at module load, before SessionContext wraps window.fetch.
const rawFetch = typeof window !== "undefined" && window.fetch ? window.fetch.bind(window) : null;

let inFlight = null;

/* Result shape:
     { ok: true,  token }                  → access token renewed
     { ok: false, expired: true }          → session is over, must log in again
     { ok: false, expired: false }         → transient (offline, 5xx); retry later */
async function requestNewToken() {
  const refreshToken = getRefreshToken();
  const accessToken = getAccessToken();

  // Nothing to prove a previous login with.
  if (!refreshToken && !accessToken) {
    return { ok: false, expired: true };
  }

  let res;
  try {
    res = await rawFetch(new URL("api/auth/refresh", API_BASE), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        // Legacy fallback: with no refresh token stored, the backend accepts the
        // (even expired) access token as proof of login and upgrades the client
        // to a real refresh token.
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(refreshToken ? { refresh_token: refreshToken } : {}),
    });
  } catch (e) {
    // Network error — the session may well still be good.
    return { ok: false, expired: false };
  }

  if (res.status === 401 || res.status === 403) {
    return { ok: false, expired: true };
  }

  if (!res.ok) {
    return { ok: false, expired: false };
  }

  let json;
  try {
    json = await res.json();
  } catch (e) {
    return { ok: false, expired: false };
  }

  const data = json && json.data;
  if (!data || !data.token) {
    return { ok: false, expired: false };
  }

  saveTokens(data);
  return { ok: true, token: data.token };
}

/**
 * Renew the access token. Concurrent callers share one network round trip and
 * all receive the same result.
 */
export function refreshAccessToken() {
  if (!rawFetch) return Promise.resolve({ ok: false, expired: false });

  if (!inFlight) {
    inFlight = requestNewToken().finally(() => {
      inFlight = null;
    });
  }

  return inFlight;
}

/**
 * Refresh only if the access token is at/near expiry. Used by the activity
 * watcher to slide an in-use session forward without hitting /refresh on every
 * mouse move.
 */
export function refreshIfExpiring(maxSkewMs = MAX_REFRESH_SKEW_MS) {
  if (!isAccessTokenExpiring(maxSkewMs)) {
    return Promise.resolve({ ok: true, token: getAccessToken(), skipped: true });
  }
  return refreshAccessToken();
}
